import { FilteredPriceBreakdownConfig } from '../redux/priceBreakdownReducer';
import { CarPricesType } from '../types/oneGraphTypes';

export const getPriceAmount = (prices: CarPricesType[], type: string) => {
  return prices?.find((element) => element?.type === type)?.price?.value || null;
};

export const shouldShowFinalPrice = (
  breakdownConfig: FilteredPriceBreakdownConfig,
  typedPrices: CarPricesType[],
) => {
  // if final Price is not enabled in CFM, do not show final price
  if (!breakdownConfig?.final?.isEnabled) return false;
  const finalPrice = getPriceAmount(typedPrices, 'final');
  // If there is no final Price data and also text override is disabled in CFM for final Price
  // Then do not show final price
  if (!finalPrice && !breakdownConfig?.final?.hasTextOverride) return false;
  return true;
};

// Main price key logic:

// NEW

// if Final Price is enabled and data available then Final price is Main
// Else if dealerPrice is enabled and data available then DealerPrice is main
// Else if regular(list) price is enabled and data available then regular(list) price is main

// USED

// if Final Price is enabled and data available then Final price is Main
// Else if dealerPrice is enabled and data available then DealerPrice is main
// Else if Retail(sale) price is enabled and data available then retail(sale) price is main
// Else if Regular(list) price is enabled and data available then regular(list) price is main

export const shouldCheckMainPrice = (
  priceKey: string,
  breakdownConfig: FilteredPriceBreakdownConfig,
  isUsedVehicle: boolean,
  showFinalPrice: boolean,
  typedPrices: CarPricesType[],
) => {
  if (showFinalPrice || !breakdownConfig) return false;
  const isDealerPriceEnabled = breakdownConfig?.dealerPrice?.isEnabled;
  const dealerPriceAmount = getPriceAmount(typedPrices, 'dealerPrice');
  const retailPriceAmount = getPriceAmount(typedPrices, 'sale');
  const hasDealerPrice = !!dealerPriceAmount && isDealerPriceEnabled;
  const isRegularEnabled = breakdownConfig?.regular?.isEnabled;
  const isRetailEnabled = breakdownConfig?.retail?.isEnabled;
  const hasRetailPrice = !!retailPriceAmount && isRetailEnabled;
  if (priceKey === 'dealerPrice') return hasDealerPrice;
  if (!isUsedVehicle && priceKey === 'list' && !hasDealerPrice) return isRegularEnabled;
  if (isUsedVehicle) {
    if (priceKey === 'sale' && !hasDealerPrice) return hasRetailPrice;
    if (priceKey === 'list' && !hasRetailPrice) return isRegularEnabled;
  }
  return false;
};

export const getVisiblePricesCount = (
  breakdownConfig: FilteredPriceBreakdownConfig,
  isUsedVehicle: boolean,
  displayRebates: boolean,
  displayRebatesUsed: boolean,
  rebateLength: number,
  typedPrices: CarPricesType[],
  showFinalPrice: boolean,
) => {
  // Count Number of price options that are enabled and also have a price amount
  const enabledPriceOptionsCount = Object.keys(breakdownConfig).reduce((count, key) => {
    const item = breakdownConfig[key as keyof FilteredPriceBreakdownConfig];
    // if item is not enabled in CFM skip
    if (!item.isEnabled) return count;
    const itemAmount = typedPrices?.find((element) => element?.type === item?.priceKey);
    const hasNoAmount = !itemAmount || !itemAmount?.price?.value;
    const isOverrideEnabled = item?.hasTextOverride;
    // if item price is not available from BE data and text override is not enabled in CFM then SKIP
    if (hasNoAmount && !isOverrideEnabled) return count;
    // if item is final Price and we're not going to show final price then skip
    if (item.priceKey === 'final' && !showFinalPrice) return count;
    return count + 1;
  }, 0);

  const isRebateEnabledInCFM = isUsedVehicle ? displayRebatesUsed : displayRebates;
  const rebatesCount = rebateLength && isRebateEnabledInCFM ? rebateLength : 0;
  return enabledPriceOptionsCount + rebatesCount;
};
